import { PhoneInput, TextInput } from "@brexhq/component-library";
import { useTheme } from "@emotion/react";
import * as React from "react";

import { Container } from "@brexhq/next/src/components/layout/Container/Container";
import { useFormSubmissionContext } from "@brexhq/next/src/components/modules/LeadForm/FormSubmissionProvider";
import { useLeadFormModuleContext } from "@brexhq/next/src/components/modules/LeadForm/LeadFormModuleProvider";
import { GTMFormPageTemplate } from "@brexhq/next/src/components/modules/LeadForm/Steps/PageTemplates/GTMFormPageTemplate";
import { LeadFormSteps } from "@brexhq/next/src/components/modules/LeadForm/machines/types";
import { LeadFormValues } from "@brexhq/next/src/components/modules/LeadForm/sharedTypes";
import isPersonalEmail from "@brexhq/next/src/utils/isPersonalEmail";

import { invalidWorkEmailErrorMessage } from "../../../../building-blocks/MarketoForm/utils";

import { SingleFlexContainer } from "./elements";
export const GTMEmail: React.FC<{ checkWorkEmail?: boolean }> = ({
  checkWorkEmail = false,
}) => {
  const { spacing } = useTheme();
  const {
    submit,
    form: { fields, clearErrors, getValues },
    setSubmitError,
    submitError,
  } = useFormSubmissionContext();

  const { customSubmitButtonLabel, customProgramName, customFormTitle } =
    useLeadFormModuleContext();

  /** Checks if fields are valid to disable */
  const validFields =
    !!getValues("firstName") &&
    !!getValues("lastName") &&
    !!getValues("companyName") &&
    !!getValues("mobileNumber") &&
    !!getValues("workEmail") &&
    !fields.mobileNumber.error &&
    !fields.workEmail.error;

  const handleEmailChange = React.useCallback(
    (value: string) => {
      fields.workEmail.onChange({ value });
      clearErrors();
      setSubmitError(null);
    },
    [fields.workEmail, clearErrors, setSubmitError],
  );

  const onSubmit = React.useCallback(
    async (event) => {
      const fieldList: (keyof LeadFormValues)[] = [
        "firstName",
        "lastName",
        "companyName",
        "mobileNumber",
        "workEmail",
      ];

      const emailFieldValue = getValues("workEmail");

      if (
        checkWorkEmail &&
        emailFieldValue &&
        isPersonalEmail(emailFieldValue)
      ) {
        setSubmitError({
          title: "Invalid email",
          message: invalidWorkEmailErrorMessage,
        });
        return;
      }

      /**
       * Handles form submission including validations.
       * Reference FormSubmissionProvider.tsx for more context
       */
      submit({
        event,
        fields: fieldList,
        prevStep: LeadFormSteps.userInfo,
      });
    },
    [submit, checkWorkEmail, getValues, setSubmitError],
  );

  return (
    <GTMFormPageTemplate
      title={customFormTitle ?? "Enter your info"}
      step={LeadFormSteps.gtmEmail}
      programName={customProgramName}
      nextAction={{
        label: customSubmitButtonLabel ?? "Continue",
        loading: false,
        disabled: !validFields,
        onClick: onSubmit,
      }}
    >
      <Container
        width="100%"
        display="flex"
        direction={{
          LRG: "row",
          SML: "column",
        }}
        margin={{
          LRG: [0, 0, "sml", 0],
          MED: [0, 0, "sml", 0],
          SML: [0, 0, "sml", 0],
        }}
        gap="sml"
      >
        <SingleFlexContainer>
          <Container display="flex" gap="sml" direction="column">
            <Container
              width="100%"
              display="flex"
              direction={{
                LRG: "row",
                SML: "column",
              }}
              margin={{
                LRG: [0, 0, "xxsml", 0],
                MED: [0, 0, "xxsml", 0],
                SML: [0, 0, "xxsml", 0],
              }}
              gap="sml"
            >
              <div style={{ flex: 1 }}>
                <TextInput
                  inputMode="text"
                  size="large"
                  autoComplete="given-name"
                  {...fields.firstName}
                />
              </div>
              <div style={{ flex: 1 }}>
                <TextInput
                  inputMode="text"
                  size="large"
                  autoComplete="family-name"
                  {...fields.lastName}
                />
              </div>
            </Container>
            <div style={{ marginBottom: spacing.SML.xxsml }}>
              <TextInput
                size="large"
                autoComplete="organization"
                {...fields.companyName}
              />
            </div>
            <div style={{ marginBottom: spacing.SML.xxsml }}>
              <TextInput
                inputMode="email"
                size="large"
                autoComplete="email"
                {...fields.workEmail}
                error={
                  fields.workEmail.error
                    ? fields.workEmail.error
                    : submitError
                    ? submitError.message
                    : null
                }
                onChange={(e) => handleEmailChange(e.value)}
                warning={
                  isPersonalEmail(getValues("workEmail") || "")
                    ? "Please use a work email address if available"
                    : null
                }
              />
            </div>
            <PhoneInput
              size="large"
              autoComplete="tel"
              {...fields.mobileNumber}
            />
          </Container>
        </SingleFlexContainer>
      </Container>
    </GTMFormPageTemplate>
  );
};
